import type { UserGroupRoles } from '@esg/esg-global-types';

import { company_label } from '../../../lib/app/company';
import { group_label } from '../../../lib/app/group';

/**
 * Pure selection model behind {@link GroupCompanyAccessPicker}, kept apart from
 * the component so the Add user and Bulk import drawers can build their payload
 * without pulling the MUI render graph, and so these helpers can be tested
 * without a DOM.
 */

/**
 * Companies selected per group, keyed by group id. A key exists for every group
 * chosen in the group picker; its `companyIds` may be empty while the admin has
 * not yet picked companies within it. Empty groups are dropped when the payload
 * is built — bare group membership with no company access is never granted from
 * a creation flow.
 *
 * The single field is deliberate headroom: per-company role overrides are a
 * deferred requirement and will land alongside `companyIds`.
 */
export interface AccessSelectionEntry {
  companyIds: string[];
}

export type AccessSelection = Record<string, AccessSelectionEntry>;

/** True when at least one company is selected in at least one group. */
export const hasAnyCompany = (selection: AccessSelection): boolean =>
  Object.values(selection).some((entry) => entry.companyIds.length > 0);

/** Selected group / company totals, for summary copy. */
export const countSelection = (
  selection: AccessSelection
): { groups: number; companies: number } => {
  const entries = Object.values(selection).filter((entry) => entry.companyIds.length > 0);
  return {
    groups: entries.length,
    companies: entries.reduce((total, entry) => total + entry.companyIds.length, 0)
  };
};

/**
 * Human-readable size of a selection, e.g. "3 companies across 2 groups".
 * Shared by the picker's summary line and the import wizard's confirm step so a
 * copy edit is a single touch; entity nouns come from the shared labels.
 */
export const describeSelection = (selection: AccessSelection): string => {
  const { groups, companies } = countSelection(selection);
  const companyWord = (companies === 1 ? company_label.one : company_label.many).toLowerCase();
  const groupWord = (groups === 1 ? group_label.one : group_label.many).toLowerCase();
  return `${companies} ${companyWord} across ${groups} ${groupWord}`;
};

/**
 * Builds the `UserGroupRoles` payload from a selection. Groups with no selected
 * company are omitted, so the denormalised `group_ids` never gains a group the
 * user has no access within. Group-admin is never granted here — promotion
 * stays a super-admin action on Edit user → Access.
 *
 * @param {AccessSelection} selection Companies selected per group.
 * @param {string} role Role applied to every selected company.
 * @param {Iterable<string> | undefined} allowedGroupIds When supplied, groups
 *   outside this set are dropped. Callers pass the groups the admin can
 *   actually see, so a stale key that the picker can neither render nor remove
 *   cannot silently grant access.
 * @returns {UserGroupRoles} Multi-group access map for createUser / createUsers.
 */
export const buildGroupsPayload = (
  selection: AccessSelection,
  role: string,
  allowedGroupIds?: Iterable<string>
): UserGroupRoles => {
  const allowed: Set<string> | null = allowedGroupIds ? new Set(allowedGroupIds) : null;
  return Object.fromEntries(
    Object.entries(selection)
      .filter(
        ([groupId, entry]) => entry.companyIds.length > 0 && (!allowed || allowed.has(groupId))
      )
      .map(([groupId, entry]) => [
        groupId,
        {
          admin: false,
          companies: Object.fromEntries(entry.companyIds.map((companyId) => [companyId, { role }]))
        }
      ])
  );
};

/**
 * Toggles one company within one group, returning a new selection. Selecting a
 * company removes it from any other group it was selected under: `company_ids`
 * is denormalised as a flat set server-side, so the same company held under two
 * groups would silently collapse there while the `groups` map kept both. Only
 * reachable on inconsistent source data, where losing the stale entry is the
 * intended outcome.
 */
export const toggleCompanySelection = (
  selection: AccessSelection,
  groupId: string,
  companyId: string
): AccessSelection => {
  const current = selection[groupId]?.companyIds ?? [];
  if (current.includes(companyId)) {
    return {
      ...selection,
      [groupId]: { companyIds: current.filter((id) => id !== companyId) }
    };
  }
  const withoutElsewhere: AccessSelection = Object.fromEntries(
    Object.entries(selection).map(([id, entry]) => [
      id,
      { companyIds: entry.companyIds.filter((selectedId) => selectedId !== companyId) }
    ])
  );
  return {
    ...withoutElsewhere,
    [groupId]: { companyIds: [...(withoutElsewhere[groupId]?.companyIds ?? []), companyId] }
  };
};

/**
 * Narrows a selection to the supplied group ids, preserving the companies
 * already chosen in the groups that survive. Removing a group from the picker
 * therefore discards its company choices rather than leaving them orphaned in
 * state where they would still reach the payload. Pure — not a state setter,
 * despite reading like one at the call site.
 */
export const withSelectedGroups = (
  selection: AccessSelection,
  groupIds: string[]
): AccessSelection =>
  Object.fromEntries(
    groupIds.map((groupId) => [groupId, selection[groupId] ?? { companyIds: [] }])
  );

/**
 * Ceiling on (rows × companies) for one bulk import.
 *
 * `createUser` updates one subscription document per (group, company) pair, and
 * those updates run sequentially inside the row's Firestore transaction — so a
 * batch costs `rows × companies` round-trips in a single request. At the 500-row
 * cap a wide selection runs into the request timeout long before anything else
 * complains, and a timeout mid-import is the least legible failure available.
 *
 * Retrying after a timeout is safe (already-created rows come back as
 * `already_exists`), so this exists to convert that timeout into a message the
 * operator can act on, not to protect data integrity.
 */
export const MAX_ACCESS_GRANTS_PER_IMPORT = 2000;

/** Subscription writes a bulk import will perform: one per row, per company. */
export const accessGrantCount = (rowCount: number, selection: AccessSelection): number =>
  rowCount * countSelection(selection).companies;
